import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Archive, ArchiveRestore, ArrowLeftRight, BookMarked, BookOpen, CalendarRange, CheckCircle2,
  FileSpreadsheet, FileText, Filter, Library, Lock, LockOpen, Pencil, PencilLine, Plus, Printer, Scale, Trash2,
  TrendingUp, Undo2, Upload,
} from 'lucide-react';
import { Button, FormField, Modal, PageHeader, Skeleton, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type CompteSCF,
  type CompteType,
  type EcritureDTO,
  type EcritureInput,
  type EcritureStatut,
  type Exercice,
  type Journal,
  type JournalType,
  type LigneEtat,
  type PalierTCR,
  type RubriqueActifSCF,
  type RubriquePassifSCF,
  type SensNormal,
  contrepasserEcriture,
  createCompte,
  createEcriture,
  createExercice,
  createJournal,
  deleteEcriture,
  deleteExercice,
  deleteJournal,
  downloadLiasse,
  getBalance,
  getBilan,
  getCompteResultat,
  getLiasse,
  importComptes,
  listComptes,
  listEcritures,
  listExercices,
  listJournaux,
  updateCompte,
  updateEcriture,
  updateExercice,
  updateJournal,
  validerEcriture,
  genererPasserelle,
  getPasserelleCertifier,
  getPasserelleReconciliation,
  listBrouillonsPeriode,
  listPasserelleRegles,
  updatePasserelleRegle,
  type PasserelleGeneration,
  type PasserelleReconciliation,
  type PasserelleType,
  listClotureMensuelle,
  cloturerMois,
  rouvrirMois,
  type MoisCloture,
} from '@/services/finance';
import { toast } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { formatDZD } from '@/utils/algeria';

const CLASSES: Record<number, string> = {
  1: 'Capitaux', 2: 'Immobilisations', 3: 'Stocks', 4: 'Tiers',
  5: 'Financiers', 6: 'Charges', 7: 'Produits', 8: 'Spéciaux',
};
const JOURNAL_TYPES: { value: JournalType; label: string }[] = [
  { value: 'ACHATS', label: 'Achats' }, { value: 'VENTES', label: 'Ventes' },
  { value: 'BANQUE', label: 'Banque' }, { value: 'CAISSE', label: 'Caisse' },
  { value: 'OPERATIONS_DIVERSES', label: 'Opérations diverses' },
];
const selectCls = 'h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const typeLabel = (t: CompteType) => (t === 'BILAN' ? 'Bilan' : 'Gestion');

const STATUT_ECR: Record<EcritureStatut, { label: string; cls: string }> = {
  BROUILLON: { label: 'Brouillon', cls: 'bg-status-working/10 text-status-working' },
  VALIDE: { label: 'Validée', cls: 'bg-status-done/10 text-status-done' },
  VERROUILLE: { label: 'Verrouillée', cls: 'bg-content-muted/10 text-content-muted' },
};

export function ComptabilitePage() {
  const [tab, setTab] = useState('plan');
  const { data: balance, isLoading } = useQuery({ queryKey: ['balance'], queryFn: getBalance });

  return (
    <>
      <PageHeader
        title="Comptabilité SCF"
        subtitle="Plan comptable SCF, journaux, exercices, écritures et états financiers"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Comptabilité SCF' }]}
        actions={<Button onClick={() => setTab('ecritures')}><Plus /> Nouvelle écriture</Button>}
      />

      <Tabs
        tabs={[
          { key: 'plan', label: 'Plan comptable', icon: <Library className="size-4" /> },
          { key: 'journaux', label: 'Journaux', icon: <BookMarked className="size-4" /> },
          { key: 'exercices', label: 'Exercices', icon: <CalendarRange className="size-4" /> },
          { key: 'ecritures', label: 'Écritures', icon: <PencilLine className="size-4" /> },
          { key: 'passerelle', label: 'Passerelle', icon: <ArrowLeftRight className="size-4" /> },
          { key: 'balance', label: 'Balance', icon: <BookOpen className="size-4" /> },
          { key: 'bilan', label: 'Bilan', icon: <Scale className="size-4" /> },
          { key: 'resultat', label: 'Compte de résultat', icon: <TrendingUp className="size-4" /> },
          { key: 'liasse', label: 'Liasse fiscale', icon: <FileText className="size-4" /> },
          { key: 'cloture', label: 'Clôture', icon: <Lock className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />

      {tab === 'plan' && <PlanComptableView />}
      {tab === 'journaux' && <JournauxView />}
      {tab === 'exercices' && <ExercicesView />}
      {tab === 'ecritures' && <EcrituresView />}
      {tab === 'passerelle' && <PasserelleView onGoToEcritures={() => setTab('ecritures')} />}
      {tab === 'bilan' && <BilanView />}
      {tab === 'resultat' && <CompteResultatView />}
      {tab === 'liasse' && <LiasseView />}
      {tab === 'cloture' && <ClotureView />}

      {tab === 'balance' && (isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <div className="border-b border-border px-5 py-3"><h2 className="font-bold text-content-primary">Balance générale</h2></div>
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Compte</th>
                <th className="px-4 py-3 label-caps">Libellé</th>
                <th className="px-4 py-3 text-right label-caps">Débit</th>
                <th className="px-4 py-3 text-right label-caps">Crédit</th>
                <th className="px-4 py-3 text-right label-caps">Solde déb.</th>
                <th className="px-4 py-3 text-right label-caps">Solde créd.</th>
              </tr>
            </thead>
            <tbody>
              {balance?.lignes.map((l) => (
                <tr key={l.compte_numero} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{l.compte_numero}</td>
                  <td className="px-4 py-2 text-content-secondary">{l.libelle}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDZD(l.total_debit, { decimals: 0 })}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDZD(l.total_credit, { decimals: 0 })}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{l.solde_debiteur > 0 ? formatDZD(l.solde_debiteur, { decimals: 0 }) : '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{l.solde_crediteur > 0 ? formatDZD(l.solde_crediteur, { decimals: 0 }) : '—'}</td>
                </tr>
              ))}
              {balance && balance.lignes.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-content-muted">Aucune écriture. Saisissez la première écriture.</td></tr>
              )}
            </tbody>
            {balance && balance.lignes.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-border bg-surface-primary font-bold">
                  <td className="px-4 py-3" colSpan={2}>Totaux</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatDZD(balance.total_debit, { decimals: 0 })}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatDZD(balance.total_credit, { decimals: 0 })}</td>
                  <td className="px-4 py-3" colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ))}
    </>
  );
}

function EtatTable({ titre, lignes, total, totalLabel, accent }: { titre: string; lignes: LigneEtat[]; total: number; totalLabel: string; accent: string }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
      <div className="border-b border-border px-5 py-3"><h2 className="font-bold" style={{ color: accent }}>{titre}</h2></div>
      <table className="w-full text-sm">
        <tbody>
          {lignes.length === 0 && (
            <tr><td className="px-4 py-8 text-center text-sm text-content-muted">Aucun mouvement.</td></tr>
          )}
          {lignes.map((l) => (
            <tr key={l.numero} className="border-b border-border last:border-0">
              <td className="px-4 py-2 font-mono text-xs text-content-muted">{l.numero}</td>
              <td className="px-4 py-2 text-content-secondary">{l.libelle}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(l.montant, { decimals: 0 })}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border bg-surface-primary font-bold">
            <td className="px-4 py-3" colSpan={2}>{totalLabel}</td>
            <td className="px-4 py-3 text-right tabular-nums" style={{ color: accent }}>{formatDZD(total, { decimals: 0 })}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const num = (v: number | string) => Number(v) || 0;

function ExportButtons() {
  const [busy, setBusy] = useState<'pdf' | 'xlsx' | null>(null);
  const run = async (f: 'pdf' | 'xlsx') => {
    setBusy(f);
    try { await downloadLiasse(f); } catch (e) { toast.error(getApiErrorMessage(e)); } finally { setBusy(null); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button onClick={() => run('pdf')} disabled={busy !== null}><Printer className="size-4" /> {busy === 'pdf' ? 'Génération…' : 'Imprimer (PDF)'}</Button>
      <Button onClick={() => run('xlsx')} disabled={busy !== null}><FileSpreadsheet className="size-4" /> {busy === 'xlsx' ? 'Export…' : 'Exporter Excel'}</Button>
    </div>
  );
}

function BilanActifTable({ rubriques, total }: { rubriques: RubriqueActifSCF[]; total: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
      <div className="border-b border-border px-5 py-3"><h2 className="font-bold text-content-primary">ACTIF</h2></div>
      <table className="w-full text-sm">
        <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
          <th className="px-4 py-2 label-caps">Rubrique</th>
          <th className="px-4 py-2 text-right label-caps">Brut</th>
          <th className="px-4 py-2 text-right label-caps">Amort./Prov.</th>
          <th className="px-4 py-2 text-right label-caps">Net</th>
        </tr></thead>
        <tbody>
          {rubriques.map((r) => (
            <tr key={r.libelle} className="border-b border-border last:border-0">
              <td className="px-4 py-2 text-content-secondary">{r.libelle}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDZD(num(r.brut), { decimals: 0 })}</td>
              <td className="px-4 py-2 text-right tabular-nums text-content-muted">{num(r.amortissement) ? `(${formatDZD(num(r.amortissement), { decimals: 0 })})` : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(num(r.net), { decimals: 0 })}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="border-t-2 border-border bg-surface-primary font-bold">
          <td className="px-4 py-3" colSpan={3}>TOTAL ACTIF NET</td>
          <td className="px-4 py-3 text-right tabular-nums text-brand-primary">{formatDZD(total, { decimals: 0 })}</td>
        </tr></tfoot>
      </table>
    </div>
  );
}

function BilanPassifTable({ rubriques, total }: { rubriques: RubriquePassifSCF[]; total: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
      <div className="border-b border-border px-5 py-3"><h2 className="font-bold text-content-primary">PASSIF</h2></div>
      <table className="w-full text-sm">
        <tbody>
          {rubriques.map((r) => (
            <tr key={r.libelle} className="border-b border-border last:border-0">
              <td className="px-4 py-2 text-content-secondary">{r.libelle}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(num(r.total), { decimals: 0 })}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr className="border-t-2 border-border bg-surface-primary font-bold">
          <td className="px-4 py-3">TOTAL PASSIF</td>
          <td className="px-4 py-3 text-right tabular-nums text-brand-primary">{formatDZD(total, { decimals: 0 })}</td>
        </tr></tfoot>
      </table>
    </div>
  );
}

function CascadeTable({ paliers }: { paliers: PalierTCR[] }) {
  const fortes = new Set(['VALEUR_AJOUTEE', 'EBE', 'RESULTAT_OPERATIONNEL', 'RESULTAT_ORDINAIRE', 'RESULTAT_NET_ORDINAIRE', 'RESULTAT_NET']);
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
      <div className="border-b border-border px-5 py-3"><h2 className="font-bold text-content-primary">Compte de résultat — cascade SCF</h2></div>
      <table className="w-full text-sm">
        <tbody>
          {paliers.map((p) => (
            <tr key={p.code} className={cn('border-b border-border last:border-0', fortes.has(p.code) && 'bg-surface-primary font-semibold')}>
              <td className="px-4 py-2 text-content-secondary">{p.libelle}</td>
              <td className={cn('px-4 py-2 text-right tabular-nums', p.code === 'RESULTAT_NET' && 'text-base font-bold text-brand-primary')}>{formatDZD(num(p.montant), { decimals: 0 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComptesNonClasses({ comptes }: { comptes?: { numero: string; libelle: string; montant: number }[] }) {
  if (!comptes || comptes.length === 0) return null;
  return (
    <div className="mx-auto max-w-xl rounded-md border border-status-stuck/40 bg-status-stuck/10 px-4 py-2 text-xs text-status-stuck">
      <p className="font-semibold">Comptes non classés au bilan (à rattacher à une rubrique) :</p>
      <ul className="mt-1 space-y-0.5">
        {comptes.map((c) => (
          <li key={c.numero} className="flex justify-between gap-3">
            <span className="font-mono">{c.numero} — {c.libelle}</span>
            <span className="tabular-nums">{formatDZD(num(c.montant), { decimals: 0 })}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BilanView() {
  const { data, isLoading } = useQuery({ queryKey: ['bilan'], queryFn: getBilan });
  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><ExportButtons /></div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BilanActifTable rubriques={data.actif} total={num(data.total_actif_net)} />
        <BilanPassifTable rubriques={data.passif} total={num(data.total_passif)} />
      </div>
      <p className={cn('text-center text-xs font-medium', data.equilibre ? 'text-status-done' : 'text-status-stuck')}>
        {data.equilibre
          ? `✓ Bilan équilibré — Total Actif net = Total Passif = ${formatDZD(num(data.total_actif_net), { decimals: 0 })}`
          : `Bilan non équilibré (écart ${formatDZD(num(data.ecart), { decimals: 0 })}).`}
      </p>
      {!data.equilibre && <ComptesNonClasses comptes={data.comptes_non_classes} />}
    </div>
  );
}

function CompteResultatView() {
  const { data, isLoading } = useQuery({ queryKey: ['compte-resultat'], queryFn: getCompteResultat });
  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end"><ExportButtons /></div>
      <CascadeTable paliers={data.cascade} />
      {!data.coherent && (
        <p className="rounded-md border border-status-stuck/40 bg-status-stuck/10 px-4 py-2 text-xs text-status-stuck">
          ⚠ Incohérence : résultat cascade {formatDZD(num(data.resultat), { decimals: 0 })} ≠ résultat bilan {formatDZD(num(data.resultat_bilan), { decimals: 0 })} — les livres ne bouclent pas.
        </p>
      )}
      <div className="rounded-lg border border-border bg-surface-secondary p-5 text-center shadow-sm">
        <p className="label-caps">Résultat net de l'exercice (cascade SCF)</p>
        <p className={cn('mt-1 text-2xl font-bold tabular-nums', num(data.resultat) >= 0 ? 'text-status-done' : 'text-status-stuck')}>
          {formatDZD(num(data.resultat))} {num(data.resultat) >= 0 ? '(bénéfice)' : '(perte)'}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EtatTable titre="CHARGES (classe 6)" lignes={data.charges} total={num(data.total_charges)} totalLabel="Total charges" accent="#E2445C" />
        <EtatTable titre="PRODUITS (classe 7)" lignes={data.produits} total={num(data.total_produits)} totalLabel="Total produits" accent="#00C875" />
      </div>
    </div>
  );
}

function LiasseView() {
  const { data, isLoading } = useQuery({ queryKey: ['liasse'], queryFn: getLiasse });
  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-content-primary">Liasse fiscale</h2>
          <p className="text-xs text-content-muted">Bilan SCF + compte de résultat (cascade) + tableaux annexes. Lecture seule — aucune donnée modifiée.</p>
        </div>
        <ExportButtons />
      </div>
      <CascadeTable paliers={data.tcr.paliers} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BilanActifTable rubriques={data.bilan.actif} total={num(data.bilan.total_actif_net)} />
        <BilanPassifTable rubriques={data.bilan.passif} total={num(data.bilan.total_passif)} />
      </div>
      <p className={cn('text-center text-xs font-medium', data.bilan.equilibre ? 'text-status-done' : 'text-status-stuck')}>
        {data.bilan.equilibre
          ? `✓ Total Actif net = Total Passif = ${formatDZD(num(data.bilan.total_actif_net), { decimals: 0 })}`
          : `Écart ${formatDZD(num(data.bilan.ecart), { decimals: 0 })}`}
      </p>
      {!data.bilan.equilibre && <ComptesNonClasses comptes={data.bilan.comptes_non_classes} />}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <h3 className="mb-2 font-semibold text-content-primary">Tableau 9 — Résultat fiscal</h3>
          <div className="flex justify-between text-sm"><span className="text-content-secondary">Résultat comptable</span><span className="tabular-nums">{formatDZD(num(data.tableau9.benefice_comptable), { decimals: 0 })}</span></div>
          <div className="flex justify-between text-sm"><span className="text-content-secondary">Réintégrations / déductions</span><span className="text-content-muted">à saisir (validation humaine)</span></div>
          <div className="mt-1 flex justify-between border-t border-border pt-1 text-sm font-bold"><span>Résultat fiscal</span><span className="tabular-nums">{formatDZD(num(data.tableau9.resultat_fiscal), { decimals: 0 })}</span></div>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
          <h3 className="mb-2 font-semibold text-content-primary">Tableaux annexes</h3>
          <ul className="space-y-1 text-sm">
            {data.annexes.map((a) => (
              <li key={a.numero} className="flex justify-between"><span className="text-content-secondary">Tableau {a.numero} — {a.titre}</span><span className="text-content-muted">{a.statut}</span></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Passerelle ------------------------------ */
const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const labelType = (t: PasserelleType) => (t === 'ventes' ? 'Ventes' : t === 'achats' ? 'Achats' : 'Paie');
/** Tonalité : un solde/écart proche de 0 est « bon » (vert), sinon « à surveiller » (rouge). */
const ecartTone = (v?: string): 'ok' | 'warn' => (Math.abs(num(v ?? '0')) < 0.005 ? 'ok' : 'warn');
/** Formate un montant (string Decimal | undefined) en DZD entier. */
const fmt0 = (v?: string) => formatDZD(num(v ?? '0'), { decimals: 0 });
/** Tonalité d'un solde CUMULÉ « en attente » : ≥ 0 = normal (vert), négatif = anomalie (rouge). */
const cumuleTone = (v?: string): 'ok' | 'warn' => (num(v ?? '0') >= -0.005 ? 'ok' : 'warn');

function resumeGeneration(type: PasserelleType, r: PasserelleGeneration): string {
  if (type === 'ventes') {
    const crees = (r.factures_creees ?? 0) + (r.encaissements_crees ?? 0);
    const deja = (r.factures_deja ?? 0) + (r.encaissements_deja ?? 0);
    return `Ventes : ${crees} écriture(s) créée(s), ${deja} déjà existante(s)${r.a_affecter ? `, ${r.a_affecter} à affecter` : ''}.`;
  }
  if (type === 'achats') {
    const crees = (r.factures_creees ?? 0) + (r.reglements_crees ?? 0) + (r.immobilisations ?? 0);
    const deja = (r.factures_deja ?? 0) + (r.reglements_deja ?? 0);
    return `Achats : ${crees} écriture(s) créée(s), ${deja} déjà existante(s)${r.a_affecter ? `, ${r.a_affecter} à affecter` : ''}.`;
  }
  return `Paie : ${r.ecriture_creee ? 1 : 0} écriture(s) créée(s), ${r.deja ? 1 : 0} déjà existante(s)${r.lignes_a_affecter ? `, ${r.lignes_a_affecter} ligne(s) à affecter` : ''}.`;
}

function MetricRow({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'ok' | 'warn' | 'neutral' }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-content-secondary">{label}</span>
      <span className={cn('font-medium tabular-nums',
        tone === 'ok' ? 'text-status-done' : tone === 'warn' ? 'text-status-stuck' : 'text-content-primary')}>{value}</span>
    </div>
  );
}

function ReconShell({ title, isLoading, hasData, children }: { title: string; isLoading: boolean; hasData: boolean; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <h3 className="mb-2 font-semibold text-content-primary">{title}</h3>
      {isLoading || !hasData ? <Skeleton className="h-32 w-full" /> : children}
    </div>
  );
}

function ReconVentesAchats({ titre, data, isLoading, sens }: {
  titre: string; data?: PasserelleReconciliation; isLoading: boolean; sens: 'ventes' | 'achats';
}) {
  const tvaBasculee = sens === 'ventes' ? data?.tva_basculee_4457 : data?.tva_basculee_4456;
  const tvaG50 = sens === 'ventes' ? data?.tva_collectee_g50 : data?.tva_deductible_g50;
  const soldeCumule = sens === 'ventes' ? data?.solde_44587_cumule : data?.solde_44586_cumule;
  const compteBascule = sens === 'ventes' ? '4457' : '4456';
  const compteAttente = sens === 'ventes' ? '44587' : '44586';
  const enAttente = sens === 'ventes' ? 'non encore encaissées' : 'non encore payées';
  const nAffecter = data?.ecritures_a_affecter?.length ?? 0;
  return (
    <ReconShell title={titre} isLoading={isLoading} hasData={!!data}>
      {data && (
        <>
          <MetricRow label={`TVA basculée → ${compteBascule}`} value={fmt0(tvaBasculee)} />
          <MetricRow label="TVA reconnue (G50)" value={fmt0(tvaG50)} />
          <MetricRow label="Écart TVA vs G50" value={fmt0(data.ecart_tva)} tone={ecartTone(data.ecart_tva)} />
          <MetricRow label={`TVA en attente cumulée (${compteAttente})`} value={fmt0(soldeCumule)} tone={cumuleTone(soldeCumule)} />
          <p className="-mt-0.5 text-[11px] text-content-muted">cumul à fin de période = TVA des factures {enAttente} (≥ 0)</p>
          <div className="mt-2 border-t border-border pt-2">
            <MetricRow label="Écritures à affecter" value={String(nAffecter)} tone={nAffecter > 0 ? 'warn' : 'ok'} />
          </div>
        </>
      )}
    </ReconShell>
  );
}

function ReconPaie({ data, isLoading }: { data?: PasserelleReconciliation; isLoading: boolean }) {
  return (
    <ReconShell title="Réconciliation Paie" isLoading={isLoading} hasData={!!data}>
      {data && (
        <>
          <MetricRow label="Brut → 631 (écart)" value={fmt0(data.ecart_brut)} tone={ecartTone(data.ecart_brut)} />
          <MetricRow label="Net → 421 (écart)" value={fmt0(data.ecart_net)} tone={ecartTone(data.ecart_net)} />
          <MetricRow label="Cotisations → 431 (écart)" value={fmt0(data.ecart_cotisations)} tone={ecartTone(data.ecart_cotisations)} />
          <MetricRow label="IRG → 442 (écart)" value={fmt0(data.ecart_irg)} tone={ecartTone(data.ecart_irg)} />
          <div className="mt-2 border-t border-border pt-2">
            <MetricRow label="Contrôle d'identité (= 0)" value={fmt0(data.controle_identite)} tone={ecartTone(data.controle_identite)} />
          </div>
        </>
      )}
    </ReconShell>
  );
}

/* C.1 — certification du plan (comptes requis présents / actifs / feuilles). */
function CertifBadge({ type }: { type: PasserelleType }) {
  const { data, isLoading } = useQuery({ queryKey: ['passerelle-certif', type], queryFn: () => getPasserelleCertifier(type) });
  const ok = data?.plan_certifie;
  return (
    <div className={cn('rounded-md border px-3 py-2',
      isLoading ? 'border-border' : ok ? 'border-status-done/40 bg-status-done/5' : 'border-status-stuck/40 bg-status-stuck/5')}>
      <div className="flex items-center gap-2 text-sm font-medium">
        {isLoading ? <span className="text-content-muted">…</span>
          : ok ? <CheckCircle2 className="size-4 text-status-done" /> : <AlertTriangle className="size-4 text-status-stuck" />}
        <span>{labelType(type)}</span>
        <span className={cn('ml-auto text-xs', ok ? 'text-status-done' : 'text-status-stuck')}>
          {isLoading ? '' : ok ? 'Plan certifié' : 'À compléter'}
        </span>
      </div>
      {data && !ok && (
        <ul className="mt-1 space-y-0.5 text-xs text-status-stuck">
          {data.problemes.map((p, i) => <li key={i}>• {p}</li>)}
        </ul>
      )}
    </div>
  );
}

function CertifierStrip() {
  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <h3 className="mb-2 font-semibold text-content-primary">Certification du plan comptable</h3>
      <p className="mb-2 text-xs text-content-muted">Chaque type doit disposer de ses comptes requis (présents, actifs, mouvementables) pour pouvoir générer.</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {(['ventes', 'achats', 'paie'] as PasserelleType[]).map((t) => <CertifBadge key={t} type={t} />)}
      </div>
    </div>
  );
}

/* C.3 — règles d'imputation éditables (nature/catégorie/rôle → compte), réservé direction/DFC. */
function ReglesConfig() {
  const qc = useQueryClient();
  const [type, setType] = useState<PasserelleType>('achats');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const { data, isLoading } = useQuery({ queryKey: ['passerelle-regles', type], queryFn: () => listPasserelleRegles(type) });
  const saveMut = useMutation({
    mutationFn: ({ cle, compte }: { cle: string; compte: string }) => updatePasserelleRegle(type, cle, compte),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['passerelle-regles', type] }); toast.success('Règle mise à jour.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-content-primary">Règles d'imputation → compte</h3>
        <div className="flex gap-1">
          {(['ventes', 'achats', 'paie'] as PasserelleType[]).map((t) => (
            <Button key={t} variant={t === type ? 'secondary' : 'ghost'} size="sm" onClick={() => { setType(t); setDraft({}); }}>{labelType(t)}</Button>
          ))}
        </div>
      </div>
      <p className="mb-2 text-xs text-content-muted">Modification réservée à la direction / DFC. Le compte doit exister, être actif et de la bonne classe.</p>
      {isLoading || !data ? <Skeleton className="h-40 w-full" /> : (
        <table className="w-full text-sm">
          <thead className="bg-surface-primary">
            <tr className="border-b border-border text-left">
              <th className="px-3 py-2 label-caps">Libellé</th>
              <th className="px-3 py-2 label-caps">Compte</th>
              <th className="px-3 py-2 label-caps">Source</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => {
              const val = draft[r.cle] ?? r.compte_numero;
              const dirty = val.trim() !== r.compte_numero && val.trim() !== '';
              return (
                <tr key={r.cle} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 text-content-secondary">{r.libelle}</td>
                  <td className="px-3 py-2">
                    <Input value={val} onChange={(e) => setDraft((d) => ({ ...d, [r.cle]: e.target.value }))} className="h-8 w-28 font-mono text-xs" />
                  </td>
                  <td className="px-3 py-2">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs', r.source === 'base' ? 'bg-status-done/10 text-status-done' : 'bg-content-muted/10 text-content-muted')}>
                      {r.source === 'base' ? 'Personnalisée' : 'Défaut'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate({ cle: r.cle, compte: val.trim() })}>
                      <Pencil className="size-4" /> Enregistrer
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PasserelleView({ onGoToEcritures }: { onGoToEcritures: () => void }) {
  const qc = useQueryClient();
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmValider, setConfirmValider] = useState(false);

  const recVentes = useQuery({ queryKey: ['passerelle-rec', 'ventes', mois, annee], queryFn: () => getPasserelleReconciliation('ventes', mois, annee) });
  const recAchats = useQuery({ queryKey: ['passerelle-rec', 'achats', mois, annee], queryFn: () => getPasserelleReconciliation('achats', mois, annee) });
  const recPaie = useQuery({ queryKey: ['passerelle-rec', 'paie', mois, annee], queryFn: () => getPasserelleReconciliation('paie', mois, annee) });
  const brouillons = useQuery({ queryKey: ['passerelle-brouillons', mois, annee], queryFn: () => listBrouillonsPeriode(mois, annee) });

  const invalidate = () =>
    ['passerelle-rec', 'passerelle-brouillons', 'ecritures', 'balance', 'bilan', 'compte-resultat'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const genMut = useMutation({
    mutationFn: (type: PasserelleType) => genererPasserelle(type, mois, annee),
    onSuccess: (res, type) => {
      invalidate();
      if (!res.plan_certifie) {
        toast.error(`Plan non certifié (${labelType(type)}) : ${(res.problemes ?? []).join(' · ') || 'comptes requis manquants'}`);
      } else {
        toast.success(resumeGeneration(type, res));
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const validerMut = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => validerEcriture(id))),
    onSuccess: (_r, ids) => { invalidate(); setSelected(new Set()); setConfirmValider(false); toast.success(`${ids.length} écriture(s) validée(s) — figées (piste d'audit).`); },
    onError: (e) => { setConfirmValider(false); toast.error(getApiErrorMessage(e)); },
  });

  const items = brouillons.data ?? [];
  const affecter: { id: string; reference: string | null; sens: string }[] = [
    ...(recVentes.data?.ecritures_a_affecter ?? []).map((x) => ({ ...x, sens: 'Ventes' })),
    ...(recAchats.data?.ecritures_a_affecter ?? []).map((x) => ({ ...x, sens: 'Achats' })),
  ];
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = items.length > 0 && items.every((e) => selected.has(e.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(items.map((e) => e.id)));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-content-primary">Passerelle comptable</h2>
          <p className="max-w-2xl text-xs text-content-muted">
            Génère les écritures comptables (en <b>brouillon</b>) à partir des factures, encaissements, règlements et
            bulletins de la période. <b>Idempotent</b> : regénérer ne crée jamais de doublon. La validation reste un geste
            humain délibéré (jamais automatique).
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-content-muted">Mois
            <select value={mois} onChange={(e) => { setMois(Number(e.target.value)); setSelected(new Set()); }} className={cn(selectCls, 'w-40')}>
              {MOIS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-content-muted">Année
            <Input type="number" value={annee} min={2000} max={2100} onChange={(e) => { setAnnee(Number(e.target.value)); setSelected(new Set()); }} className="h-10 w-28" />
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h3 className="font-semibold text-content-primary">Générer la période — {MOIS[mois - 1]} {annee}</h3>
        <p className="mb-3 text-xs text-content-muted">Crée les écritures brouillon manquantes ; les existantes sont laissées intactes.</p>
        <div className="flex flex-wrap gap-2">
          {(['ventes', 'achats', 'paie'] as PasserelleType[]).map((t) => (
            <Button key={t} variant="secondary" disabled={genMut.isPending} onClick={() => genMut.mutate(t)}>
              <ArrowLeftRight className="size-4" /> Générer {labelType(t)}
            </Button>
          ))}
        </div>
      </div>

      <CertifierStrip />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ReconVentesAchats titre="Réconciliation Ventes" sens="ventes" data={recVentes.data} isLoading={recVentes.isLoading} />
        <ReconVentesAchats titre="Réconciliation Achats" sens="achats" data={recAchats.data} isLoading={recAchats.isLoading} />
        <ReconPaie data={recPaie.data} isLoading={recPaie.isLoading} />
      </div>

      {affecter.length > 0 && (
        <div className="rounded-lg border border-status-stuck/40 bg-status-stuck/5 p-4 shadow-sm">
          <h3 className="font-semibold text-content-primary">Écritures « à affecter » ({affecter.length})</h3>
          <p className="mb-2 text-xs text-content-muted">Produit/charge non résolu (nature/catégorie non mappée). Renseignez le compte manquant dans l'éditeur d'écriture.</p>
          <ul className="space-y-1 text-sm">
            {affecter.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 border-b border-border/50 py-1 last:border-0">
                <span><span className="text-content-muted">{a.sens} — </span><span className="font-mono text-xs">{a.reference ?? a.id.slice(0, 8)}</span></span>
                <Button variant="ghost" size="sm" onClick={onGoToEcritures}>Corriger dans Écritures</Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
          <h3 className="font-bold text-content-primary">Brouillons de la période ({items.length})</h3>
          <div className="flex items-center gap-2">
            <Button variant="success" size="sm" disabled={selected.size === 0 || validerMut.isPending} onClick={() => setConfirmValider(true)}>
              <CheckCircle2 className="size-4" /> Valider la sélection ({selected.size})
            </Button>
            <Button variant="ghost" size="sm" onClick={onGoToEcritures}>Ouvrir dans Écritures</Button>
          </div>
        </div>
        {brouillons.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-content-muted">Aucun brouillon sur cette période. Lancez une génération ci-dessus.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Tout sélectionner" /></th>
                <th className="px-4 py-3 label-caps">Date</th>
                <th className="px-4 py-3 label-caps">Pièce</th>
                <th className="px-4 py-3 label-caps">Libellé</th>
                <th className="px-4 py-3 text-right label-caps">Montant</th>
                <th className="px-4 py-3 label-caps">Statut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const total = e.lignes.reduce((s, l) => s + (Number(l.montant_debit) || 0), 0);
                const st = STATUT_ECR[e.statut];
                return (
                  <tr key={e.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2"><input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} aria-label="Sélectionner" /></td>
                    <td className="px-4 py-2 tabular-nums">{e.date_ecriture}</td>
                    <td className="px-4 py-2 font-mono text-xs">{e.numero_piece ?? '—'}</td>
                    <td className="px-4 py-2 text-content-secondary">{e.libelle}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatDZD(total, { decimals: 0 })}</td>
                    <td className="px-4 py-2"><span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', st.cls)}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ReglesConfig />

      <Modal
        open={confirmValider}
        onOpenChange={setConfirmValider}
        title="Valider les brouillons sélectionnés"
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmValider(false)}>Annuler</Button>
          <Button variant="success" loading={validerMut.isPending} onClick={() => validerMut.mutate([...selected])}>Confirmer la validation</Button>
        </>}
      >
        <p className="text-sm text-content-secondary">
          Vous allez valider <b>{selected.size}</b> écriture(s). La validation est <b>définitive</b> (écriture figée, piste
          d'audit) — vérifiez d'abord les brouillons « à affecter ». Geste comptable délibéré.
        </p>
      </Modal>
    </div>
  );
}

/* --------------------------- Clôture mensuelle --------------------------- */
function ClotureView() {
  const qc = useQueryClient();
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const { data, isLoading } = useQuery({ queryKey: ['cloture-mensuelle', annee], queryFn: () => listClotureMensuelle(annee) });
  const [confirmMois, setConfirmMois] = useState<number | null>(null);
  const [brouillons, setBrouillons] = useState<number | null>(null);
  const [rouvrirM, setRouvrirM] = useState<number | null>(null);
  const [motif, setMotif] = useState('');

  const invalidate = () =>
    ['cloture-mensuelle', 'ecritures', 'passerelle-brouillons', 'passerelle-rec'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const closeMut = useMutation({
    mutationFn: (mois: number) => cloturerMois(mois, annee),
    onSuccess: () => { invalidate(); setConfirmMois(null); setBrouillons(null); toast.success('Mois clôturé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const openMut = useMutation({
    mutationFn: (m: { mois: number; motif: string }) => rouvrirMois(m.mois, annee, m.motif),
    onSuccess: () => { invalidate(); setRouvrirM(null); setMotif(''); toast.success('Mois rouvert — réouverture tracée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const demanderCloture = async (mois: number) => {
    setConfirmMois(mois);
    setBrouillons(null);
    try { setBrouillons((await listBrouillonsPeriode(mois, annee)).length); } catch { setBrouillons(0); }
  };

  const ligneAction = (m: MoisCloture): string => {
    if (m.reouvert_le) {
      return `Rouvert le ${m.reouvert_le.slice(0, 10)}${m.reouvert_par ? ` par ${m.reouvert_par}` : ''}${m.motif_reouverture ? ` — « ${m.motif_reouverture} »` : ''}`;
    }
    if (m.cloture_le) return `Clôturé le ${m.cloture_le.slice(0, 10)}${m.cloture_par ? ` par ${m.cloture_par}` : ''}`;
    return '—';
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-content-primary">Clôture mensuelle</h2>
          <p className="max-w-2xl text-xs text-content-muted">
            Un mois <b>clôturé</b> verrouille toute écriture/saisie datée dedans (facture, encaissement,
            règlement, génération passerelle). La <b>réouverture</b> est réservée à l'administrateur, exige
            un motif et reste tracée.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-xs text-content-muted">Année
          <Input type="number" value={annee} min={2000} max={2100} onChange={(e) => setAnnee(Number(e.target.value))} className="h-10 w-28" />
        </label>
      </div>

      {isLoading || !data ? <Skeleton className="h-96 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Mois</th>
                <th className="px-4 py-3 label-caps">Statut</th>
                <th className="px-4 py-3 label-caps">Dernière action</th>
                <th className="px-4 py-3 text-right label-caps">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => {
                const clos = m.statut === 'CLOTURE';
                return (
                  <tr key={m.mois} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-medium text-content-primary">{MOIS[m.mois - 1]}</td>
                    <td className="px-4 py-2">
                      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                        clos ? 'bg-content-muted/10 text-content-muted' : 'bg-status-done/10 text-status-done')}>
                        {clos ? <><Lock className="size-3" /> Clôturé</> : <><LockOpen className="size-3" /> Ouvert</>}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-content-secondary">{ligneAction(m)}</td>
                    <td className="px-4 py-2 text-right">
                      {clos
                        ? (isAdmin
                          ? <Button variant="ghost" size="sm" onClick={() => { setRouvrirM(m.mois); setMotif(''); }}><LockOpen className="size-4" /> Rouvrir</Button>
                          : <span className="text-xs text-content-muted">Réouverture : admin</span>)
                        : <Button variant="secondary" size="sm" onClick={() => demanderCloture(m.mois)}><Lock className="size-4" /> Clôturer</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={confirmMois !== null}
        onOpenChange={(o) => { if (!o) { setConfirmMois(null); setBrouillons(null); } }}
        title={confirmMois ? `Clôturer ${MOIS[confirmMois - 1]} ${annee}` : 'Clôturer le mois'}
        footer={<>
          <Button variant="secondary" onClick={() => { setConfirmMois(null); setBrouillons(null); }}>Annuler</Button>
          <Button loading={closeMut.isPending} onClick={() => confirmMois && closeMut.mutate(confirmMois)}>Clôturer le mois</Button>
        </>}
      >
        <p className="text-sm text-content-secondary">Le mois sera verrouillé : plus aucune écriture ni saisie datée dedans (réouverture admin possible).</p>
        {brouillons === null
          ? <p className="mt-2 text-xs text-content-muted">Vérification des brouillons…</p>
          : brouillons > 0
            ? <p className="mt-2 rounded-md border border-status-stuck/40 bg-status-stuck/10 px-3 py-2 text-xs text-status-stuck">⚠ Il reste {brouillons} brouillon(s) non validé(s) sur ce mois. Clôturer quand même ?</p>
            : <p className="mt-2 text-xs text-status-done">Aucun brouillon en attente sur ce mois.</p>}
      </Modal>

      <Modal
        open={rouvrirM !== null}
        onOpenChange={(o) => { if (!o) { setRouvrirM(null); setMotif(''); } }}
        title={rouvrirM ? `Rouvrir ${MOIS[rouvrirM - 1]} ${annee}` : 'Rouvrir le mois'}
        footer={<>
          <Button variant="secondary" onClick={() => { setRouvrirM(null); setMotif(''); }}>Annuler</Button>
          <Button variant="danger" loading={openMut.isPending} disabled={!motif.trim()}
            onClick={() => rouvrirM && openMut.mutate({ mois: rouvrirM, motif: motif.trim() })}>Rouvrir (tracé)</Button>
        </>}
      >
        <p className="text-sm text-content-secondary">La réouverture est <b>tracée</b> (qui / quand / motif). Indiquez le motif :</p>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
          placeholder="Motif de la réouverture (obligatoire)"
          className="mt-2 w-full rounded-md border border-border bg-surface-secondary px-3 py-2 text-sm" />
      </Modal>
    </div>
  );
}

/* -------------------------------- Écritures ------------------------------ */
interface EcritureFormValues {
  journal_id: string;
  date_ecriture: string;
  numero_piece: string;
  libelle: string;
  lignes: { compte_numero: string; libelle: string; montant_debit: number; montant_credit: number }[];
}

function emptyEcritureForm(): EcritureFormValues {
  return {
    journal_id: '', date_ecriture: new Date().toISOString().slice(0, 10), numero_piece: '', libelle: '',
    lignes: [
      { compte_numero: '', libelle: '', montant_debit: 0, montant_credit: 0 },
      { compte_numero: '', libelle: '', montant_debit: 0, montant_credit: 0 },
    ],
  };
}

function EcrituresView() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filtres, setFiltres] = useState<{ journal_id?: string; statut?: EcritureStatut; date_debut?: string; date_fin?: string }>({});

  const { data: comptes } = useQuery({ queryKey: ['comptes-scf'], queryFn: () => listComptes() });
  const { data: journaux } = useQuery({ queryKey: ['journaux'], queryFn: listJournaux });
  const { data: page, isLoading } = useQuery({ queryKey: ['ecritures', filtres], queryFn: () => listEcritures(filtres) });
  const invalidate = () => {
    ['ecritures', 'balance', 'bilan', 'compte-resultat'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const { register, handleSubmit, control, reset, setValue } = useForm<EcritureFormValues>({ defaultValues: emptyEcritureForm() });
  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' });
  // useWatch (et non watch) : garantit le recalcul réactif des totaux à chaque frappe.
  const lignesW = useWatch({ control, name: 'lignes' });
  const journalId = useWatch({ control, name: 'journal_id' });
  const libelleW = useWatch({ control, name: 'libelle' });
  const { totalD, totalC, ecart, equilibre, hasMontant } = useMemo(() => {
    const td = (lignesW ?? []).reduce((s, l) => s + (Number(l.montant_debit) || 0), 0);
    const tc = (lignesW ?? []).reduce((s, l) => s + (Number(l.montant_credit) || 0), 0);
    return { totalD: td, totalC: tc, ecart: td - tc, equilibre: td === tc && td > 0, hasMontant: td > 0 || tc > 0 };
  }, [lignesW]);

  const openCreate = () => { setEditId(null); reset(emptyEcritureForm()); setOpen(true); };
  const openEdit = (e: EcritureDTO) => {
    setEditId(e.id);
    reset({
      journal_id: e.journal_id ?? '', date_ecriture: e.date_ecriture, numero_piece: e.numero_piece ?? '', libelle: e.libelle,
      lignes: e.lignes.map((l) => ({ compte_numero: l.compte_numero, libelle: l.libelle ?? '', montant_debit: Number(l.montant_debit), montant_credit: Number(l.montant_credit) })),
    });
    setOpen(true);
  };

  const saveMut = useMutation({
    mutationFn: (v: EcritureFormValues) => {
      const payload: EcritureInput = {
        journal_id: v.journal_id, date_ecriture: v.date_ecriture, numero_piece: v.numero_piece || null, libelle: v.libelle,
        lignes: v.lignes
          .filter((l) => l.compte_numero || Number(l.montant_debit) || Number(l.montant_credit))
          .map((l) => ({ compte_numero: l.compte_numero, libelle: l.libelle || null, montant_debit: Number(l.montant_debit) || 0, montant_credit: Number(l.montant_credit) || 0 })),
      };
      return editId ? updateEcriture(editId, payload) : createEcriture(payload);
    },
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editId ? 'Brouillon modifié.' : 'Écriture enregistrée (brouillon).'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validerMut = useMutation({ mutationFn: (id: string) => validerEcriture(id), onSuccess: () => { invalidate(); toast.success('Écriture validée — figée (piste d\'audit).'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const delMut = useMutation({ mutationFn: (id: string) => deleteEcriture(id), onSuccess: () => { invalidate(); toast.success('Brouillon supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const cpMut = useMutation({
    mutationFn: (id: string) => contrepasserEcriture(id),
    onSuccess: (cp) => { invalidate(); toast.success('Contre-passation générée (brouillon) — vérifiez puis validez.'); openEdit(cp); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  // Choix du journal → propose sa contrepartie par défaut sur une ligne vide.
  const onJournal = (id: string) => {
    setValue('journal_id', id);
    const j = journaux?.find((x) => x.id === id);
    if (j?.compte_contrepartie) {
      const idx = (lignesW ?? []).findIndex((l) => !l.compte_numero);
      if (idx >= 0) setValue(`lignes.${idx}.compte_numero`, j.compte_contrepartie);
    }
  };

  const codeJournal = (id: string | null) => journaux?.find((j) => j.id === id)?.code ?? '—';
  const items = page?.items ?? [];

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-content-muted"><Filter className="size-3.5" /> Filtrer</span>
        <select value={filtres.journal_id ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, journal_id: e.target.value || undefined }))} className={cn(selectCls, 'w-auto')}>
          <option value="">Tous les journaux</option>
          {journaux?.map((j) => <option key={j.id} value={j.id}>{j.code} · {j.libelle}</option>)}
        </select>
        <select value={filtres.statut ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, statut: (e.target.value || undefined) as EcritureStatut | undefined }))} className={cn(selectCls, 'w-auto')}>
          <option value="">Tous statuts</option>
          <option value="BROUILLON">Brouillons</option>
          <option value="VALIDE">Validées</option>
        </select>
        <Input type="date" value={filtres.date_debut ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, date_debut: e.target.value || undefined }))} className="w-auto" />
        <Input type="date" value={filtres.date_fin ?? ''} onChange={(e) => setFiltres((f) => ({ ...f, date_fin: e.target.value || undefined }))} className="w-auto" />
        <div className="ml-auto"><Button onClick={openCreate}><Plus /> Nouvelle écriture</Button></div>
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 label-caps">N°</th>
              <th className="px-4 py-2.5 label-caps">Date</th>
              <th className="px-4 py-2.5 label-caps">Journal</th>
              <th className="px-4 py-2.5 label-caps">Libellé</th>
              <th className="px-4 py-2.5 text-right label-caps">Montant</th>
              <th className="px-4 py-2.5 label-caps">Statut</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {items.map((e) => {
                const montant = e.lignes.reduce((s, l) => s + Number(l.montant_debit), 0);
                const st = STATUT_ECR[e.statut];
                const brouillon = e.statut === 'BROUILLON';
                return (
                  <Fragment key={e.id}>
                    <tr className="border-b border-border last:border-0 hover:bg-surface-hover">
                      <td className="px-4 py-2 font-mono text-xs font-semibold">{e.numero ?? '—'}</td>
                      <td className="px-4 py-2 tabular-nums text-content-secondary">{e.date_ecriture}</td>
                      <td className="px-4 py-2 font-mono text-xs">{codeJournal(e.journal_id)}</td>
                      <td className="px-4 py-2">
                        <button onClick={() => setExpandedId(expandedId === e.id ? null : e.id)} className="text-left hover:underline">{e.libelle}</button>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatDZD(montant, { decimals: 0 })}</td>
                      <td className="px-4 py-2"><span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-semibold', st.cls)}>{st.label}</span></td>
                      <td className="px-4 py-2"><div className="flex items-center justify-end gap-1">
                        {brouillon ? (
                          <>
                            <button onClick={() => validerMut.mutate(e.id)} className="rounded p-1.5 text-status-done hover:bg-status-done/10" title="Valider" aria-label="Valider"><CheckCircle2 className="size-4" /></button>
                            <button onClick={() => openEdit(e)} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" title="Modifier" aria-label="Modifier"><Pencil className="size-4" /></button>
                            <button onClick={() => { if (window.confirm('Supprimer ce brouillon ?')) delMut.mutate(e.id); }} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" title="Supprimer" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                          </>
                        ) : (
                          <button onClick={() => { if (window.confirm('Générer la contre-passation de cette écriture validée ?')) cpMut.mutate(e.id); }} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" title="Contre-passer" aria-label="Contre-passer"><Undo2 className="size-4" /></button>
                        )}
                      </div></td>
                    </tr>
                    {expandedId === e.id && (
                      <tr className="bg-surface-primary/40"><td colSpan={7} className="px-4 py-3">
                        <table className="w-full text-xs">
                          <thead><tr className="text-left text-content-muted">
                            <th className="py-1 font-medium">Compte</th><th className="py-1 font-medium">Libellé</th>
                            <th className="py-1 text-right font-medium">Débit</th><th className="py-1 text-right font-medium">Crédit</th>
                          </tr></thead>
                          <tbody>
                            {e.lignes.map((l) => (
                              <tr key={l.id}>
                                <td className="py-1 font-mono">{l.compte_numero}</td>
                                <td className="py-1 text-content-secondary">{l.libelle || e.libelle}</td>
                                <td className="py-1 text-right tabular-nums">{Number(l.montant_debit) > 0 ? formatDZD(l.montant_debit, { decimals: 0 }) : '—'}</td>
                                <td className="py-1 text-right tabular-nums">{Number(l.montant_credit) > 0 ? formatDZD(l.montant_credit, { decimals: 0 }) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {e.numero_piece && <p className="mt-2 text-xs text-content-muted">Pièce : {e.numero_piece}{e.reference_doc ? ` · réf. ${e.reference_doc}` : ''}</p>}
                      </td></tr>
                    )}
                  </Fragment>
                );
              })}
              {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-content-muted">Aucune écriture. Cliquez sur « Nouvelle écriture ».</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editId ? 'Modifier le brouillon' : 'Nouvelle écriture comptable'} size="xl"
        footer={<>
          <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={handleSubmit((v) => saveMut.mutate(v))} loading={saveMut.isPending} disabled={!journalId || !libelleW?.trim() || !hasMontant}>{editId ? 'Enregistrer' : 'Enregistrer le brouillon'}</Button>
        </>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <FormField label="Journal" required>
            <select value={journalId} onChange={(e) => onJournal(e.target.value)} className={selectCls}>
              <option value="">— Journal —</option>
              {journaux?.map((j) => <option key={j.id} value={j.id}>{j.code} · {j.libelle}</option>)}
            </select>
          </FormField>
          <FormField label="Date" required><Input type="date" {...register('date_ecriture')} /></FormField>
          <FormField label="N° de pièce"><Input {...register('numero_piece')} placeholder="ex : FA-2026-014" /></FormField>
          <FormField label="Libellé" required><Input {...register('libelle')} /></FormField>
        </div>

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Lignes d'écriture</p>
            <Button variant="ghost" size="sm" onClick={() => append({ compte_numero: '', libelle: '', montant_debit: 0, montant_credit: 0 })}><Plus className="size-4" /> Ligne</Button>
          </div>
          <div className="space-y-2">
            {fields.map((f, i) => {
              const dVal = Number(lignesW?.[i]?.montant_debit) || 0;
              const cVal = Number(lignesW?.[i]?.montant_credit) || 0;
              const setMontant = (sens: 'montant_debit' | 'montant_credit', autre: 'montant_debit' | 'montant_credit', val: string) => {
                const v = val === '' ? 0 : Number(val);
                setValue(`lignes.${i}.${sens}`, Number.isFinite(v) ? v : 0, { shouldDirty: true });
                if (v > 0) setValue(`lignes.${i}.${autre}`, 0, { shouldDirty: true });
              };
              return (
                <div key={f.id} className="grid grid-cols-12 gap-2">
                  <select {...register(`lignes.${i}.compte_numero`)} className="col-span-4 h-10 rounded-md border border-border bg-surface-secondary px-2 text-sm">
                    <option value="">— Compte —</option>
                    {comptes?.map((c) => <option key={c.numero} value={c.numero}>{c.numero} · {c.libelle_fr}</option>)}
                  </select>
                  <Input placeholder="Libellé ligne" className="col-span-3" {...register(`lignes.${i}.libelle`)} />
                  <Input type="number" step="1" placeholder="Débit" className="col-span-2 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={cVal > 0} title={cVal > 0 ? 'Cette ligne est au crédit. Videz le crédit pour saisir un débit.' : 'Montant au débit'}
                    value={lignesW?.[i]?.montant_debit ?? 0}
                    onChange={(e) => setMontant('montant_debit', 'montant_credit', e.target.value)} />
                  <Input type="number" step="1" placeholder="Crédit" className="col-span-2 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={dVal > 0} title={dVal > 0 ? 'Cette ligne est au débit. Videz le débit pour saisir un crédit.' : 'Montant au crédit'}
                    value={lignesW?.[i]?.montant_credit ?? 0}
                    onChange={(e) => setMontant('montant_credit', 'montant_debit', e.target.value)} />
                  <Button variant="ghost" size="icon" className="col-span-1 h-10 text-status-stuck" onClick={() => remove(i)} disabled={fields.length <= 2} aria-label="Retirer"><Trash2 className="size-4" /></Button>
                </div>
              );
            })}
          </div>
          <div className={cn('mt-3 flex flex-wrap items-center justify-end gap-6 rounded-md px-4 py-2.5 text-sm', equilibre ? 'bg-status-done/10' : 'bg-status-working/10')}>
            <span>Débit : <span className="font-semibold tabular-nums">{formatDZD(totalD)}</span></span>
            <span>Crédit : <span className="font-semibold tabular-nums">{formatDZD(totalC)}</span></span>
            <span>Écart : <span className={cn('font-semibold tabular-nums', ecart === 0 ? 'text-status-done' : 'text-status-working')}>{formatDZD(ecart)}</span></span>
            <span className={cn('font-bold', equilibre ? 'text-status-done' : 'text-status-working')}>{equilibre ? '✓ Équilibrée' : 'À équilibrer avant validation'}</span>
          </div>
          <p className="mt-2 text-xs text-content-muted"><strong>Une ligne = un débit OU un crédit</strong> ; la contrepartie va sur une autre ligne (ex. 607 au débit 5 000, et 401 au crédit 5 000 sur la ligne suivante). Un <strong>brouillon peut être enregistré même déséquilibré</strong> ; l'équilibre n'est exigé qu'à la <strong>validation</strong>, qui fige l'écriture. Seules les écritures validées alimentent la balance, le bilan et le TCR.</p>
        </div>
      </Modal>
    </div>
  );
}

/* ----------------------------- Plan comptable ---------------------------- */
function PlanComptableView() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [classe, setClasse] = useState<number | ''>('');
  const [inclureInactifs, setInclureInactifs] = useState(false);
  const [open, setOpen] = useState(false);
  const [editNumero, setEditNumero] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const empty = { numero: '', libelle_fr: '', libelle_ar: '', classe: 4, type_compte: '' as '' | CompteType, sens_normal: '' as '' | SensNormal, compte_parent: '' };
  const [form, setForm] = useState(empty);

  const { data, isLoading } = useQuery({
    queryKey: ['comptes-scf', q, classe, inclureInactifs],
    queryFn: () => listComptes({ q: q || undefined, classe: classe === '' ? undefined : classe, inclure_inactifs: inclureInactifs }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['comptes-scf'] });

  const saveMut = useMutation({
    mutationFn: () => {
      const base = {
        libelle_fr: form.libelle_fr.trim(),
        libelle_ar: form.libelle_ar.trim() || null,
        classe: Number(form.classe),
        type_compte: form.type_compte || null,
        sens_normal: form.sens_normal || null,
        compte_parent: form.compte_parent.trim() || null,
      };
      return editNumero ? updateCompte(editNumero, base) : createCompte({ numero: form.numero.trim(), ...base });
    },
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editNumero ? 'Compte modifié.' : 'Compte créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const archiveMut = useMutation({
    mutationFn: (c: CompteSCF) => updateCompte(c.numero, { is_active: !c.is_active }),
    onSuccess: () => { invalidate(); toast.success('Compte mis à jour.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const importMut = useMutation({
    mutationFn: (f: File) => importComptes(f),
    onSuccess: (r) => { invalidate(); toast.success(`Import : ${r.crees} créé(s), ${r.mis_a_jour} mis à jour, ${r.ignores} ignoré(s)${r.erreurs.length ? `, ${r.erreurs.length} erreur(s)` : ''}.`); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const openCreate = () => { setEditNumero(null); setForm(empty); setOpen(true); };
  const openEdit = (c: CompteSCF) => {
    setEditNumero(c.numero);
    setForm({ numero: c.numero, libelle_fr: c.libelle_fr, libelle_ar: c.libelle_ar ?? '', classe: c.classe, type_compte: c.type_compte, sens_normal: c.sens_normal, compte_parent: c.compte_parent ?? '' });
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Rechercher (numéro ou libellé)…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />
        <select value={classe} onChange={(e) => setClasse(e.target.value === '' ? '' : Number(e.target.value))} className={cn(selectCls, 'w-auto')}>
          <option value="">Toutes les classes</option>
          {[1, 2, 3, 4, 5, 6, 7].map((k) => <option key={k} value={k}>{k} · {CLASSES[k]}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-content-secondary">
          <input type="checkbox" checked={inclureInactifs} onChange={(e) => setInclureInactifs(e.target.checked)} /> Archivés
        </label>
        <div className="ml-auto flex gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importMut.mutate(f); e.target.value = ''; }} />
          <Button variant="secondary" onClick={() => fileRef.current?.click()} loading={importMut.isPending}><Upload className="size-4" /> Importer CSV</Button>
          <Button onClick={openCreate}><Plus /> Nouveau compte</Button>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 label-caps">Numéro</th>
              <th className="px-4 py-2.5 label-caps">Libellé</th>
              <th className="px-4 py-2.5 label-caps">Classe</th>
              <th className="px-4 py-2.5 label-caps">Type</th>
              <th className="px-4 py-2.5 text-center label-caps">Sens</th>
              <th className="px-4 py-2.5 label-caps">Parent</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data?.map((c) => (
                <tr key={c.numero} className={cn('border-b border-border last:border-0 hover:bg-surface-hover', !c.is_active && 'opacity-50')}>
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{c.numero}</td>
                  <td className="px-4 py-2">{c.libelle_fr}{!c.is_active && <span className="ml-2 rounded bg-content-muted/10 px-1.5 py-0.5 text-[10px] uppercase text-content-muted">archivé</span>}</td>
                  <td className="px-4 py-2 text-content-secondary">{c.classe} · {CLASSES[c.classe] ?? '—'}</td>
                  <td className="px-4 py-2 text-content-secondary">{typeLabel(c.type_compte)}</td>
                  <td className="px-4 py-2 text-center font-mono text-xs">{c.sens_normal === 'DEBIT' ? 'D' : 'C'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-content-muted">{c.compte_parent ?? '—'}</td>
                  <td className="px-4 py-2"><div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(c)} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" aria-label="Modifier"><Pencil className="size-4" /></button>
                    <button onClick={() => archiveMut.mutate(c)} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" title={c.is_active ? 'Archiver' : 'Réactiver'} aria-label={c.is_active ? 'Archiver' : 'Réactiver'}>{c.is_active ? <Archive className="size-4" /> : <ArchiveRestore className="size-4" />}</button>
                  </div></td>
                </tr>
              ))}
              {data && data.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-content-muted">Aucun compte. Créez un compte ou importez le plan SCF (CSV).</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editNumero ? `Modifier le compte ${editNumero}` : 'Nouveau compte'} size="lg"
        footer={<>
          <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
          <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!form.numero.trim() || !form.libelle_fr.trim()}>{editNumero ? 'Enregistrer' : 'Créer'}</Button>
        </>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Numéro" required><Input value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} disabled={!!editNumero} placeholder="ex : 411001" /></FormField>
          <FormField label="Classe" required>
            <select value={form.classe} onChange={(e) => setForm({ ...form, classe: Number(e.target.value) })} className={selectCls}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((k) => <option key={k} value={k}>{k} · {CLASSES[k]}</option>)}
            </select>
          </FormField>
          <div className="sm:col-span-2"><FormField label="Libellé (FR)" required><Input value={form.libelle_fr} onChange={(e) => setForm({ ...form, libelle_fr: e.target.value })} /></FormField></div>
          <div className="sm:col-span-2"><FormField label="Libellé (AR, optionnel)"><Input value={form.libelle_ar} onChange={(e) => setForm({ ...form, libelle_ar: e.target.value })} dir="rtl" /></FormField></div>
          <FormField label="Type (sinon déduit)">
            <select value={form.type_compte} onChange={(e) => setForm({ ...form, type_compte: e.target.value as '' | CompteType })} className={selectCls}>
              <option value="">Auto (selon classe)</option>
              <option value="BILAN">Bilan</option>
              <option value="RESULTAT">Gestion</option>
            </select>
          </FormField>
          <FormField label="Sens normal (sinon déduit)">
            <select value={form.sens_normal} onChange={(e) => setForm({ ...form, sens_normal: e.target.value as '' | SensNormal })} className={selectCls}>
              <option value="">Auto (selon classe)</option>
              <option value="DEBIT">Débiteur</option>
              <option value="CREDIT">Créditeur</option>
            </select>
          </FormField>
          <div className="sm:col-span-2"><FormField label="Compte parent (optionnel)"><Input value={form.compte_parent} onChange={(e) => setForm({ ...form, compte_parent: e.target.value })} placeholder="ex : 411" /></FormField></div>
        </div>
        <p className="mt-3 text-xs text-content-muted">Import CSV : entête <code>numero,libelle,classe</code> (colonnes <code>type</code>, <code>sens</code>, <code>libelle_ar</code>, <code>parent</code> optionnelles ; séparateur <code>,</code> ou <code>;</code>). Idempotent : les comptes existants sont mis à jour.</p>
      </Modal>
    </div>
  );
}

/* -------------------------------- Journaux ------------------------------- */
function JournauxView() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const empty = { code: '', libelle: '', type: 'OPERATIONS_DIVERSES' as JournalType, compte_contrepartie: '' };
  const [form, setForm] = useState(empty);
  const { data, isLoading } = useQuery({ queryKey: ['journaux'], queryFn: listJournaux });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['journaux'] });

  const saveMut = useMutation({
    mutationFn: () => {
      const p = { code: form.code.trim(), libelle: form.libelle.trim(), type: form.type, compte_contrepartie: form.compte_contrepartie.trim() || null };
      return editId ? updateJournal(editId, p) : createJournal(p);
    },
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editId ? 'Journal modifié.' : 'Journal créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: (id: string) => deleteJournal(id), onSuccess: () => { invalidate(); toast.success('Journal supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (j: Journal) => { setEditId(j.id); setForm({ code: j.code, libelle: j.libelle, type: j.type, compte_contrepartie: j.compte_contrepartie ?? '' }); setOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={openCreate}><Plus /> Nouveau journal</Button></div>
      {isLoading ? <Skeleton className="h-48 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 label-caps">Code</th>
              <th className="px-4 py-2.5 label-caps">Libellé</th>
              <th className="px-4 py-2.5 label-caps">Type</th>
              <th className="px-4 py-2.5 label-caps">Contrepartie</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data?.map((j) => (
                <tr key={j.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{j.code}</td>
                  <td className="px-4 py-2">{j.libelle}</td>
                  <td className="px-4 py-2 text-content-secondary">{JOURNAL_TYPES.find((t) => t.value === j.type)?.label ?? j.type}</td>
                  <td className="px-4 py-2 font-mono text-xs text-content-muted">{j.compte_contrepartie ?? '—'}</td>
                  <td className="px-4 py-2"><div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(j)} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-content-primary" aria-label="Modifier"><Pencil className="size-4" /></button>
                    <button onClick={() => { if (window.confirm(`Supprimer le journal ${j.code} ?`)) delMut.mutate(j.id); }} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                  </div></td>
                </tr>
              ))}
              {data && data.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-content-muted">Aucun journal.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={open} onOpenChange={setOpen} title={editId ? 'Modifier le journal' : 'Nouveau journal'}
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!form.code.trim() || !form.libelle.trim()}>{editId ? 'Enregistrer' : 'Créer'}</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Code" required><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ex : BQ" /></FormField>
          <FormField label="Type" required>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as JournalType })} className={selectCls}>
              {JOURNAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </FormField>
          <div className="sm:col-span-2"><FormField label="Libellé" required><Input value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} /></FormField></div>
          <div className="sm:col-span-2"><FormField label="Compte de contrepartie par défaut (optionnel)"><Input value={form.compte_contrepartie} onChange={(e) => setForm({ ...form, compte_contrepartie: e.target.value })} placeholder="ex : 512" /></FormField></div>
        </div>
      </Modal>
    </div>
  );
}

/* -------------------------------- Exercices ------------------------------ */
function ExercicesView() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ annee: new Date().getFullYear(), date_debut: '', date_fin: '' });
  const { data, isLoading } = useQuery({ queryKey: ['exercices'], queryFn: listExercices });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['exercices'] });

  const saveMut = useMutation({
    mutationFn: () => createExercice({ annee: Number(form.annee), date_debut: form.date_debut || null, date_fin: form.date_fin || null }),
    onSuccess: () => { invalidate(); setOpen(false); toast.success('Exercice créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const statutMut = useMutation({
    mutationFn: (ex: Exercice) => updateExercice(ex.id, { statut: ex.statut === 'OUVERT' ? 'CLOTURE' : 'OUVERT' }),
    onSuccess: () => { invalidate(); toast.success('Exercice mis à jour.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: (id: string) => deleteExercice(id), onSuccess: () => { invalidate(); toast.success('Exercice supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { setForm({ annee: new Date().getFullYear(), date_debut: '', date_fin: '' }); setOpen(true); }}><Plus /> Nouvel exercice</Button></div>
      {isLoading ? <Skeleton className="h-48 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left">
              <th className="px-4 py-2.5 label-caps">Année</th>
              <th className="px-4 py-2.5 label-caps">Début</th>
              <th className="px-4 py-2.5 label-caps">Fin</th>
              <th className="px-4 py-2.5 label-caps">Statut</th>
              <th className="px-4 py-2.5" />
            </tr></thead>
            <tbody>
              {data?.map((ex) => (
                <tr key={ex.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2 font-semibold">{ex.annee}</td>
                  <td className="px-4 py-2 tabular-nums text-content-secondary">{ex.date_debut ?? '—'}</td>
                  <td className="px-4 py-2 tabular-nums text-content-secondary">{ex.date_fin ?? '—'}</td>
                  <td className="px-4 py-2">
                    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', ex.statut === 'OUVERT' ? 'bg-status-done/10 text-status-done' : 'bg-content-muted/10 text-content-muted')}>
                      {ex.statut === 'OUVERT' ? <LockOpen className="size-3" /> : <Lock className="size-3" />}{ex.statut === 'OUVERT' ? 'Ouvert' : 'Clôturé'}
                    </span>
                  </td>
                  <td className="px-4 py-2"><div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => statutMut.mutate(ex)}>{ex.statut === 'OUVERT' ? <><Lock className="size-4" /> Clôturer</> : <><LockOpen className="size-4" /> Rouvrir</>}</Button>
                    <button onClick={() => { if (window.confirm(`Supprimer l'exercice ${ex.annee} ?`)) delMut.mutate(ex.id); }} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                  </div></td>
                </tr>
              ))}
              {data && data.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-content-muted">Aucun exercice. Créez l'exercice courant.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <Modal open={open} onOpenChange={setOpen} title="Nouvel exercice"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}>Créer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField label="Année" required><Input type="number" step="1" value={form.annee} onChange={(e) => setForm({ ...form, annee: Number(e.target.value) })} /></FormField>
          <FormField label="Début (optionnel)"><Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} /></FormField>
          <FormField label="Fin (optionnel)"><Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} /></FormField>
        </div>
        <p className="mt-3 text-xs text-content-muted">Sans dates, l'exercice couvre l'année civile (1ᵉʳ janvier → 31 décembre). Un exercice clôturé n'acceptera plus d'écriture (Phase 2).</p>
      </Modal>
    </div>
  );
}
